import { useState, useEffect, useCallback } from "react"; 
import axios from "axios"; 
import { useNavigate } from "react-router-dom"; 

export default function LogViewer() {
  const [serial, setSerial] = useState("");
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const userRole = localStorage.getItem("role");

  // Mit useCallback sorgen wir dafür, dass fetchLogs nur dann neu erstellt wird, wenn sich eine der Abhängigkeiten ändert
  const fetchLogs = useCallback(async () => {
    const authToken = localStorage.getItem("authToken");

    try {
      const res = await axios.get(`http://localhost:4000/api/logs/${serial}`, {
        headers: {
          Authorization: `Bearer ${authToken}`  // Authentifizierung via Bearer Token
        }
      });
      setLogs(res.data.reverse()); // newest entries first
    } catch (err) {
      setError("Failed to load logs");
    }
  }, [serial]); // Abhängig von `serial`, damit es erneut aufgerufen wird, wenn `serial` geändert wird

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");  // Hol das Token aus dem LocalStorage

    // Wenn kein Token vorhanden ist oder das Token undefined ist, zur Login-Seite weiterleiten
    if (!authToken || authToken === "undefined") {
      navigate("/");  // Weiterleitung zur Login-Seite
    }
    
    // Intervall für das automatische Laden der Logs (z. B. alle 10 Sekunden)
    const interval = setInterval(() => {
      if (isValidSerial(serial)) {
        fetchLogs(); // Ruft die Logs alle 5 Sekunden ab, wenn `serial` gesetzt ist
      }
    }, 5000); // 5 Sekunden Intervall für das Abrufen der Logs

    // Cleanup (Intervalle stoppen, wenn die Komponente entladen wird)
    return () => clearInterval(interval);

  }, [navigate, serial, fetchLogs]);  // fetchLogs und serial sind die Abhängigkeiten

  const isValidSerial = (serial) => {
    const pattern = /^[Cc]\d{6}\.\d{5}$/;
    return pattern.test(serial);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Header */}
      <header className="...">
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.href = "/"}> 
            <img src="/swissphone-logo.png" alt="Swissphone" className="h-12" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-gray-700">
            OTA Server Log Viewer
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {userRole === "admin" && (
            <button
              onClick={() => window.location.href = "/admin"}
              className="bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded text-sm shadow"
            >
              User Management
            </button>
          )}
          <span className="text-sm text-gray-500">Logged in as <strong>{localStorage.getItem("email")}</strong></span>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Input Section */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <input
            type="text"
            placeholder="Enter serial number (e.g. C202441.01813)"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            onKeyDown={(e) => {
                if (e.key === "Enter" && isValidSerial(serial)) {
                  fetchLogs();
                }
              }}
          />
          <button
            onClick={() => {
              if (isValidSerial(serial)) fetchLogs();
              else alert("Please enter a valid serial number (e.g. C202434.00802)");
            }}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg shadow disabled:opacity-50"
            disabled={!isValidSerial(serial)}
          >
            Load Logs
          </button>
        </div>

{/* Auto-refresh notice */}
{isValidSerial(serial) && (
  <p className="text-sm text-gray-500 mb-6">
    Logs are automatically refreshed every 5 seconds
  </p>
)}


        {/* Logs */}
        <div className="grid gap-4">
          {logs.length > 0 ? (
            logs.map((log, i) => (
              <div
                key={i}
                className={`rounded-xl p-4 shadow border-l-4 ${
                    log.message.includes("Programming completed")
                      ? "bg-green-50 border-green-400"
                      : log.message.includes("ERROR")
                      ? "bg-red-50 border-red-400"
                      : "bg-white border-gray-200"
                  }`}
              >
                <div className="text-xs text-gray-400 mb-1">{log.timestamp}</div>
                <div className="text-sm leading-relaxed">
                  <span>{log.message}</span>
                </div>
              </div>
            ))
          ) : (
            <p>No logs found for this serial number.</p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-red-600 mt-4 text-center">{error}</p>
        )}
      </main>
    </div>
  );
}
